import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { useDndMonitor } from "@dnd-kit/core";
import { getSceneState } from "../../stores/sceneStore";
import { getHistoryActions, getHistoryStore } from "../../stores/historyStore";
import { getAssetById } from "../../stores/assetStore";
import { getSceneRecipeUiMetadataById } from "../../utils/sceneRecipes";
import { applyTerrainPreset } from "../../utils/applyTerrainPreset";
import { logger } from "../../utils/logger";
import {
  beginLibrarySceneLoadProgress,
  endLibrarySceneLoadProgress,
  reportLibrarySceneLoadProgress,
} from "../../utils/librarySceneLoadProgressEvents";
import { getLibraryPanelInflightActions } from "../../stores/libraryPanelInflightStore";
import {
  liftWorldRootBodiesOntoFlatFloorPlane,
  liftWorldRootBodiesOntoStandardTerrain,
} from "../../utils/reposeBodiesOnTerrain";
import { getStandardFloorHField } from "../../utils/terrainFloor";
import { sampleHFieldHeightAtModelXY } from "../../utils/hfieldModelSampling";
import {
  MOBILE_LIBRARY_DRAG_CANCEL_DROP_ID,
  SCENE_LIBRARY_DROPZONE_ID,
} from "../../constants/libraryDragDndIds";
import { dispatchCritterToast } from "../scene/sceneMisc";
import { debugLibraryDrop } from "../scene/sceneConstants";
import { getClientPointFromEvent } from "../scene/sceneViewportInput";
import {
  isPointerOverLibraryPanel,
  shouldSuppressMeshAssetSceneLibraryDrop,
} from "../scene/sceneLibraryDropHelpers";
import { isLibraryGripperDrag } from "../../utils/libraryGripperAttachTargets";
import {
  ensureBodyBoundsAboveSupport,
  handleLibraryDrop,
  resetLibraryDropRefs,
  waitForSceneObjectRef,
} from "./libraryDropPlacementCommit";
import {
  resolveDropPlacementFromViewport,
  resolvePlacementFrame as resolvePlacementFrameHelper,
} from "./libraryDropPlacementMath";
import {
  clearLibraryAttachMarkers as clearLibraryAttachMarkersHelper,
  clearLibraryDropShadow as clearLibraryDropShadowHelper,
  disposeObjectTree,
  ensureLibraryAttachMarkersGroup,
  ensureLibraryDropShadow,
  updateGripperAttachTargetsPreview,
} from "./libraryDropPlacementMarkers";

export function useLibraryDropPlacement({
  cameraRef,
  hfieldsForTerrainRepose,
  liftBodiesWithTerrainEdits,
  modelGroupRef,
  rendererRef,
  sceneObjectsRef,
  sceneRef,
  useYUp,
}) {
  const libraryDragDataRef = useRef(null);
  const libraryDragPointerRef = useRef(null);
  const libraryDropShadowRef = useRef(null);
  const libraryDropPlaneRef = useRef(new THREE.Plane());
  const libraryDragRafRef = useRef(null);
  const libraryDragStartPointerRef = useRef(null);
  const libraryLastValidDropPointRef = useRef(null);
  const libraryLastValidDropSupportZRef = useRef(null);
  const libraryLastValidDropSupportRef = useRef(null);
  const libraryAttachMarkersRef = useRef(null);
  const libraryGripperAttachHoverSiteRef = useRef(null);
  const libraryFallbackPlacementFrameRef = useRef(null);
  const hadStandardFloorTerrainRef = useRef(false);

  const resetDragRefs = React.useCallback(() => {
    resetLibraryDropRefs({
      libraryDragDataRef,
      libraryDragPointerRef,
      libraryDragStartPointerRef,
      libraryLastValidDropPointRef,
      libraryLastValidDropSupportZRef,
      libraryLastValidDropSupportRef,
    });
  }, []);

  const clearLibraryDropShadow = React.useCallback(() => {
    clearLibraryDropShadowHelper(libraryDropShadowRef);
  }, []);

  const clearLibraryAttachMarkers = React.useCallback(() => {
    clearLibraryAttachMarkersHelper({
      libraryAttachMarkersRef,
      libraryGripperAttachHoverSiteRef,
    });
  }, []);

  const resolvePlacementFrame = React.useCallback(
    () =>
      resolvePlacementFrameHelper({
        modelGroupRef,
        sceneRef,
        fallbackPlacementFrameRef: libraryFallbackPlacementFrameRef,
        useYUp,
      }),
    [modelGroupRef, sceneRef, useYUp]
  );

  const resolveLibraryDropPointFromClientPoint = React.useCallback(
    (clientPoint, options = {}) => {
      const { allowViewportFallback = false } = options;
      const canvas = rendererRef.current?.domElement;
      const rect = canvas?.getBoundingClientRect?.();
      if (!clientPoint || !rect || rect.width <= 0 || rect.height <= 0 || !cameraRef.current) {
        return null;
      }
      const placementFrame = resolvePlacementFrame();
      if (!placementFrame) return null;

      const clientX = THREE.MathUtils.clamp(clientPoint.x, rect.left, rect.right);
      const clientY = THREE.MathUtils.clamp(clientPoint.y, rect.top, rect.bottom);
      const placement = resolveDropPlacementFromViewport({
        u: (clientX - rect.left) / rect.width,
        v: (clientY - rect.top) / rect.height,
        allowViewportFallback,
        camera: cameraRef.current,
        scene: sceneRef.current,
        placementFrame,
        sceneObjectsRef,
        dropPlane: libraryDropPlaneRef.current,
      });
      if (!placement) return null;

      return {
        modelPoint: placement.modelPoint,
        worldPoint: placement.worldPoint,
        support: placement.support,
      };
    },
    [cameraRef, rendererRef, resolvePlacementFrame, sceneObjectsRef, sceneRef]
  );

  const updateLibraryDropShadow = React.useCallback(
    (options = {}) => {
      const { allowViewportFallback = true } = options;
      const dragData = libraryDragDataRef.current;
      if (!dragData || dragData.itemType === "terrainPreset") {
        clearLibraryDropShadow();
        return null;
      }

      const canvas = rendererRef.current?.domElement;
      const rect = canvas?.getBoundingClientRect?.();
      if (!rect || rect.width <= 0 || rect.height <= 0 || !cameraRef.current) {
        clearLibraryDropShadow();
        return null;
      }
      const placementFrame = resolvePlacementFrame();
      if (!placementFrame) {
        clearLibraryDropShadow();
        return null;
      }

      const pointer = libraryDragPointerRef.current;
      if (isPointerOverLibraryPanel(pointer)) {
        clearLibraryDropShadow();
        return null;
      }
      if (shouldSuppressMeshAssetSceneLibraryDrop(dragData, pointer)) {
        clearLibraryDropShadow();
        return null;
      }

      const margin = 8;
      const clientX = pointer
        ? THREE.MathUtils.clamp(pointer.x, rect.left + margin, rect.right - margin)
        : rect.left + rect.width * 0.5;
      const clientY = pointer
        ? THREE.MathUtils.clamp(pointer.y, rect.top + margin, rect.bottom - margin)
        : rect.top + rect.height * 0.62;
      const placement = resolveDropPlacementFromViewport({
        u: (clientX - rect.left) / rect.width,
        v: (clientY - rect.top) / rect.height,
        allowViewportFallback,
        camera: cameraRef.current,
        scene: sceneRef.current,
        placementFrame,
        sceneObjectsRef,
        dropPlane: libraryDropPlaneRef.current,
      });
      if (!placement) {
        clearLibraryDropShadow();
        return null;
      }

      debugLibraryDrop("preview", {
        supportName: placement.supportHit?.supportObjectName || "",
        supportZ: placement.supportHit?.supportZ ?? null,
        usedTerrainRay: !!placement.terrainRayModelPoint,
        terrainRayModelPoint: placement.terrainRayModelPoint
          ? [
              placement.terrainRayModelPoint.x,
              placement.terrainRayModelPoint.y,
              placement.terrainRayModelPoint.z,
            ]
          : null,
        modelPoint: [...placement.modelPoint],
        terrainHeight: placement.terrainHeight,
        shouldUseTerrainHeight: placement.shouldUseTerrainHeight,
      });

      libraryLastValidDropPointRef.current = [...placement.modelPoint];
      libraryLastValidDropSupportZRef.current = placement.support.z;
      libraryLastValidDropSupportRef.current = { ...placement.support };

      const shadow = ensureLibraryDropShadow({ sceneRef, libraryDropShadowRef });
      if (!shadow) return null;
      const planeNormal = placement.supportHit?.worldNormal || libraryDropPlaneRef.current.normal;
      const shadowWorldPoint = placement.worldPoint.clone().addScaledVector(planeNormal, 0.003);
      shadow.position.copy(shadowWorldPoint);
      shadow.scale.set(1, 1, 1);
      shadow.visible = true;
      return placement.modelPoint;
    },
    [
      cameraRef,
      clearLibraryDropShadow,
      rendererRef,
      resolvePlacementFrame,
      sceneObjectsRef,
      sceneRef,
    ]
  );

  const updateAttachTargetsPreview = React.useCallback(() => {
    updateGripperAttachTargetsPreview({
      cameraRef,
      clearAttachMarkers: clearLibraryAttachMarkers,
      ensureAttachMarkersGroup: () =>
        ensureLibraryAttachMarkersGroup({ sceneRef, libraryAttachMarkersRef }),
      libraryDragDataRef,
      libraryDragPointerRef,
      libraryGripperAttachHoverSiteRef,
      rendererRef,
      sceneRef,
    });
  }, [cameraRef, clearLibraryAttachMarkers, rendererRef, sceneRef]);

  const runLibraryDragPlacementPreview = React.useCallback(
    (options = {}) => {
      updateAttachTargetsPreview();
      const ground = updateLibraryDropShadow(options);
      if (libraryGripperAttachHoverSiteRef.current && libraryDropShadowRef.current) {
        libraryDropShadowRef.current.visible = false;
      }
      return ground;
    },
    [updateAttachTargetsPreview, updateLibraryDropShadow]
  );

  const stopLibraryDragLoop = React.useCallback(() => {
    if (libraryDragRafRef.current != null) {
      cancelAnimationFrame(libraryDragRafRef.current);
      libraryDragRafRef.current = null;
    }
  }, []);

  const startLibraryDragLoop = React.useCallback(() => {
    if (libraryDragRafRef.current != null) return;
    const tick = () => {
      if (!libraryDragDataRef.current) {
        libraryDragRafRef.current = null;
        return;
      }
      runLibraryDragPlacementPreview();
      libraryDragRafRef.current = requestAnimationFrame(tick);
    };
    libraryDragRafRef.current = requestAnimationFrame(tick);
  }, [runLibraryDragPlacementPreview]);

  const resetPreviewState = React.useCallback(() => {
    resetDragRefs();
    stopLibraryDragLoop();
    clearLibraryDropShadow();
    clearLibraryAttachMarkers();
  }, [clearLibraryAttachMarkers, clearLibraryDropShadow, resetDragRefs, stopLibraryDragLoop]);

  useEffect(() => {
    const syncLibraryDragPointerFromEvent = (event) => {
      if (!libraryDragDataRef.current) return;
      const pt = getClientPointFromEvent(event);
      if (pt) libraryDragPointerRef.current = pt;
    };

    const capturePassiveTouch = { capture: true, passive: true };
    window.addEventListener("pointermove", syncLibraryDragPointerFromEvent, true);
    window.addEventListener("touchmove", syncLibraryDragPointerFromEvent, capturePassiveTouch);
    window.addEventListener("touchend", syncLibraryDragPointerFromEvent, capturePassiveTouch);
    window.addEventListener("touchcancel", syncLibraryDragPointerFromEvent, capturePassiveTouch);

    return () => {
      window.removeEventListener("pointermove", syncLibraryDragPointerFromEvent, true);
      window.removeEventListener("touchmove", syncLibraryDragPointerFromEvent, capturePassiveTouch);
      window.removeEventListener("touchend", syncLibraryDragPointerFromEvent, capturePassiveTouch);
      window.removeEventListener(
        "touchcancel",
        syncLibraryDragPointerFromEvent,
        capturePassiveTouch
      );
    };
  }, []);

  useDndMonitor({
    onDragStart: (event) => {
      const dragData = event?.active?.data?.current;
      if (!dragData) return;
      const isTerrainPresetDrag =
        dragData.dragSource === "terrainPanel" && dragData.itemType === "terrainPreset";
      if (dragData.dragSource !== "objectLibrary" && !isTerrainPresetDrag) {
        return;
      }
      const startPointer = getClientPointFromEvent(event?.activatorEvent);
      libraryDragDataRef.current = dragData;
      libraryDragStartPointerRef.current = startPointer;
      libraryDragPointerRef.current = startPointer;
      startLibraryDragLoop();
    },
    onDragMove: () => {},
    onDragCancel: resetPreviewState,
    onDragEnd: async (event) => {
      const dragData = libraryDragDataRef.current;
      const delta = event?.delta;
      const startPointer = libraryDragStartPointerRef.current;
      if (
        !libraryDragPointerRef.current &&
        startPointer &&
        Number.isFinite(delta?.x) &&
        Number.isFinite(delta?.y)
      ) {
        libraryDragPointerRef.current = {
          x: startPointer.x + delta.x,
          y: startPointer.y + delta.y,
        };
      }
      const pointerAtDrop = libraryDragPointerRef.current;

      if (event?.over?.id === MOBILE_LIBRARY_DRAG_CANCEL_DROP_ID) {
        resetPreviewState();
        return;
      }

      const droppedOnScene = event?.over?.id === SCENE_LIBRARY_DROPZONE_ID;
      const droppedOnLibraryPanel = isPointerOverLibraryPanel(pointerAtDrop);
      const canvas = rendererRef.current?.domElement;
      const rect = canvas?.getBoundingClientRect?.();
      const pointerInsideCanvas = !!(
        pointerAtDrop &&
        rect &&
        pointerAtDrop.x >= rect.left &&
        pointerAtDrop.x <= rect.right &&
        pointerAtDrop.y >= rect.top &&
        pointerAtDrop.y <= rect.bottom
      );
      let shouldPlace = (droppedOnScene || pointerInsideCanvas) && !droppedOnLibraryPanel;
      if (shouldSuppressMeshAssetSceneLibraryDrop(dragData, pointerAtDrop)) {
        shouldPlace = false;
      }

      if (dragData?.itemType === "terrainPreset") {
        resetPreviewState();
        if (!shouldPlace || !dragData.presetKey) return;
        try {
          applyTerrainPreset(dragData.presetKey);
        } catch (error) {
          logger.error("[Scene] Failed to apply terrain preset from drag", error);
          dispatchCritterToast("Terrain could not be applied.");
        }
        return;
      }

      let dropPoint = libraryLastValidDropPointRef.current
        ? [...libraryLastValidDropPointRef.current]
        : null;
      if (!dropPoint) {
        dropPoint = runLibraryDragPlacementPreview({ allowViewportFallback: false });
      }
      const dropSupport = libraryLastValidDropSupportRef.current || {
        z: libraryLastValidDropSupportZRef.current,
      };
      if (
        Array.isArray(dropPoint) &&
        dropPoint.length === 3 &&
        !dropSupport?.bodyUuid &&
        (!dropSupport?.objectName || dropSupport.objectName === "sceneFloor")
      ) {
        const floorHField = getStandardFloorHField(getSceneState()?.hfields);
        const terrainHeight = floorHField
          ? sampleHFieldHeightAtModelXY(floorHField, dropPoint[0], dropPoint[1])
          : null;
        if (terrainHeight != null && Number.isFinite(terrainHeight)) {
          dropPoint = [dropPoint[0], dropPoint[1], terrainHeight];
          dropSupport.z = terrainHeight;
          dropSupport.objectName = "sceneFloor";
        }
        debugLibraryDrop("commit terrain sample", {
          dropPoint,
          terrainHeight,
          floorHField: !!floorHField,
          dropSupport,
        });
      }
      debugLibraryDrop("commit before insert", {
        itemType: dragData?.itemType,
        objectName: dragData?.object?.name,
        dropPoint,
        dropSupport,
        hadLastPreview: !!libraryLastValidDropPointRef.current,
      });

      const robotAttachmentSiteUuid =
        dragData?.itemType === "libraryObject" && isLibraryGripperDrag(dragData)
          ? libraryGripperAttachHoverSiteRef.current
          : null;
      resetPreviewState();
      if (!dragData || !shouldPlace || !dropPoint) return;

      const historyActions = getHistoryActions();
      const historyWasGrouping = getHistoryStore().getState().isGrouping;
      const catalogObjectId =
        dragData.itemType === "libraryObject" ? String(dragData.object?.id || "") : "";
      const meshEntry =
        dragData.itemType === "meshAsset" && dragData.entryId
          ? getAssetById(dragData.entryId)
          : null;
      const progressLabel =
        dragData.itemType === "presetScene"
          ? String(
              getSceneRecipeUiMetadataById(dragData.recipeId)?.scenarioLabel ||
                dragData.previewName ||
                dragData.recipeId ||
                "Preset"
            )
          : dragData.itemType === "meshAsset"
            ? String(dragData.previewName || meshEntry?.name || dragData.entryId || "Mesh asset")
            : String(
                dragData.object?.displayName ||
                  dragData.object?.name ||
                  dragData.previewName ||
                  "Asset"
              );
      if (!historyWasGrouping) {
        historyActions.beginGroup(getSceneState());
      }
      try {
        if (catalogObjectId) {
          getLibraryPanelInflightActions().incrementInflightForCatalogId(catalogObjectId);
        }
        beginLibrarySceneLoadProgress({ label: progressLabel, catalogId: catalogObjectId });
        const placedBodyUuid = await handleLibraryDrop(dragData, dropPoint, {
          robotAttachmentSiteUuid: robotAttachmentSiteUuid || undefined,
          onProgress: reportLibrarySceneLoadProgress,
        });
        debugLibraryDrop("commit inserted", { placedBodyUuid });
        if (placedBodyUuid) {
          await waitForSceneObjectRef(sceneObjectsRef, placedBodyUuid);
          const stillWorldRoot = (getSceneState().worldBodies || []).some((wb) => {
            const id = typeof wb === "string" ? wb : wb?.uuid;
            return id === placedBodyUuid;
          });
          debugLibraryDrop("commit post-wait", {
            placedBodyUuid,
            stillWorldRoot,
            hasSceneObj: !!sceneObjectsRef.current?.[placedBodyUuid],
            storePosition: getSceneState().bodies?.[placedBodyUuid]?.position || null,
          });
          if (stillWorldRoot) {
            let corrected = false;
            for (let attempt = 0; attempt < 4 && !corrected; attempt += 1) {
              corrected = ensureBodyBoundsAboveSupport({
                bodyUuid: placedBodyUuid,
                support: dropSupport,
                modelGroupRef,
                sceneObjectsRef,
              });
              debugLibraryDrop("commit correction attempt", {
                placedBodyUuid,
                attempt,
                corrected,
                storePosition: getSceneState().bodies?.[placedBodyUuid]?.position || null,
              });
              if (!corrected) {
                await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
              }
            }
          }
        }
      } catch (error) {
        logger.error("[Scene] Failed to place dragged library item", error);
        dispatchCritterToast(
          "Could not place item. Try a different location or check if physics is running."
        );
      } finally {
        endLibrarySceneLoadProgress();
        if (catalogObjectId) {
          getLibraryPanelInflightActions().decrementInflightForCatalogId(catalogObjectId);
        }
        if (!historyWasGrouping) {
          historyActions.endGroup(getSceneState());
        }
      }
    },
  });

  useEffect(() => {
    const hfieldsSnapshot = getSceneState()?.hfields ?? hfieldsForTerrainRepose;
    const hasStandardTerrain = !!getStandardFloorHField(hfieldsSnapshot);
    const hadStandardTerrainBefore = hadStandardFloorTerrainRef.current;
    hadStandardFloorTerrainRef.current = hasStandardTerrain;

    if (!liftBodiesWithTerrainEdits) return;
    const id = requestAnimationFrame(() => {
      if (hasStandardTerrain) {
        liftWorldRootBodiesOntoStandardTerrain(sceneObjectsRef, modelGroupRef);
      } else if (hadStandardTerrainBefore) {
        liftWorldRootBodiesOntoFlatFloorPlane(sceneObjectsRef, modelGroupRef, 0);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [hfieldsForTerrainRepose, liftBodiesWithTerrainEdits, modelGroupRef, sceneObjectsRef]);

  useEffect(() => {
    return () => {
      if (libraryDragRafRef.current != null) {
        cancelAnimationFrame(libraryDragRafRef.current);
        libraryDragRafRef.current = null;
      }
      disposeObjectTree(libraryDropShadowRef);
      disposeObjectTree(libraryAttachMarkersRef);
    };
  }, []);

  useEffect(() => {
    libraryFallbackPlacementFrameRef.current = null;
  }, [useYUp]);

  return { resolveLibraryDropPointFromClientPoint };
}
